import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Phone, Globe, Search } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { getCollegeListingSeoSettings } from '../lib/seoManager';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';
import toast from 'react-hot-toast';

const StateColleges = () => {
  const { state } = useParams();
  const [selectedStream, setSelectedStream] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  const streams = ['all', 'Engineering', 'Medical', 'Management', 'Arts', 'Law'];

  // State information mapping
  const stateInfo: { [key: string]: any } = {
    'rajasthan': {
      name: 'Rajasthan',
      description: 'Rajasthan is home to some of India\'s premier educational institutions, offering excellent opportunities in engineering, medical, and management education.',
      image: '/rajasthan2.webp?auto=compress&cs=tinysrgb&w=1200' 
    },
    'maharashtra': {
      name: 'Maharashtra',
      description: 'Maharashtra leads in higher education with world-class institutions and research facilities across various disciplines.',
      image: '/maharashtra 2.webp?auto=compress&cs=tinysrgb&w=1200'
    }, 
    'delhi': {
      name: 'Delhi',
      description: 'Delhi houses some of India\'s most prestigious educational institutions including IITs, AIIMS, and top universities.',
      image: '/delhi 2.webp?auto=compress&cs=tinysrgb&w=1200'
    },
    'karnataka': {
      name: 'Karnataka',
      description: 'Karnataka is known for its excellent engineering and medical colleges, particularly in Bangalore.',
      image: '/karnatka 2.webp?auto=compress&cs=tinysrgb&w=1200'
    },
    'tamil-nadu': {
      name: 'Tamil Nadu',
      description: 'Tamil Nadu offers world-class education with renowned institutions in engineering, medical, and arts.',
      image: '/tamil nadu 2.webp?auto=compress&cs=tinysrgb&w=1200'
    },
    'gujarat': {
      name: 'Gujarat',
      description: 'Gujarat has excellent educational infrastructure with top engineering and management institutes.',
      image: '/tamil nadu 2.webp?auto=compress&cs=tinysrgb&w=1200'
    }
  };

  useEffect(() => {
    fetchColleges();
  }, [state, selectedStream]);

  const fetchColleges = async () => {
    if (!state) return;
    
    try {
      setLoading(true);
      
      // Convert URL parameter to proper state name
      const stateName = state.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      const filters: any = { state: stateName };
      if (selectedStream !== 'all') {
        filters.category = selectedStream;
      }
      
      const data = await collegeService.getAll(filters);
      setColleges(data);
      
      if (data.length === 0) {
        toast.info(`No colleges found in ${stateName}${selectedStream !== 'all' ? ` for ${selectedStream}` : ''}`);
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to load colleges');
      setColleges([]);
    } finally {
      setLoading(false);
    }
  };

  const currentState = stateInfo[state as string] || {
    name: state?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown',
    description: 'Explore colleges in this state.',
    image: 'public/westbengal 2.webp?auto=compress&cs=tinysrgb&w=1200'
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading colleges in {currentState.name}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={`Top Colleges in ${currentState.name} - Ratings & Reviews | Grad Vikalp`}
        description={`Discover the best colleges in ${currentState.name}. Compare ratings, fees, facilities, and read authentic student reviews. Find engineering, medical, and other top colleges in ${currentState.name}.`}
        keywords={`colleges in ${currentState.name.toLowerCase()}, ${currentState.name.toLowerCase()} colleges, top colleges in ${currentState.name.toLowerCase()}, engineering colleges in ${currentState.name.toLowerCase()}, medical colleges in ${currentState.name.toLowerCase()}, college reviews, college ratings`}
        url={`/colleges/state/${state}`}
        type="website"
      />
      <div className="container py-5">
        {/* Back Button */}
        <Link
          to="/"
          className="btn btn-link text-primary text-decoration-none mb-4 p-0"
        >
          <ArrowLeft className="me-2" size={16} />
          Back to Home
        </Link>

        {/* State Header */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="position-relative">
            <OptimizedImage
              src={currentState.image}
              alt={`Top colleges in ${currentState.name} - College campus view`}
              title={`Best colleges in ${currentState.name} | Grad Vikalp College Guide`}
              className="card-img-top"
              style={{ height: '300px', objectFit: 'cover' }}
              priority={true}
            />
            <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50"></div>
            <div className="position-absolute bottom-0 start-0 p-4 text-white">
              <h1 className="display-5 fw-bold mb-2">{currentState.name} Colleges</h1>
              <p className="h5 opacity-90">{colleges.length} colleges found</p>
            </div>
          </div>

          <div className="card-body p-4">
            <p className="text-muted mb-0">{currentState.description}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold text-dark">
                  Filter by Stream
                </label>
                <select
                  value={selectedStream}
                  onChange={(e) => setSelectedStream(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {streams.map(stream => (
                    <option key={stream} value={stream}>
                      {stream === 'all' ? 'All Streams' : stream}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-dark">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-muted">
            Showing {colleges.length} college{colleges.length !== 1 ? 's' : ''} in {currentState.name}
            {selectedStream !== 'all' && ` for ${selectedStream}`}
          </p>
        </div>

        {/* Colleges Grid */}
        <div className="row g-4">
          {colleges.map((college) => (
            <div key={college.id} className="col-lg-6">
              <div className="card border-0 shadow-lg h-100 college-card">
                <div className="row g-0 h-100">
                  <div className="col-md-5">
                    <OptimizedImage
                      src={college.image_url}
                      alt={`${college.name} - ${college.category} college in ${college.location}, ${college.state}`}
                      title={`${college.name} | ${college.type} ${college.category} College | Rating: ${college.rating}/5`}
                      className="img-fluid h-100 w-100"
                      style={{ objectFit: 'cover', minHeight: '250px' }}
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="col-md-7">
                    <div className="card-body p-4 h-100 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h3 className="h5 fw-bold text-dark mb-1">{college.name}</h3>
                          <div className="d-flex align-items-center gap-3 text-muted small">
                            <div className="d-flex align-items-center gap-1">
                              <MapPin size={14} />
                              <span>{college.location}</span>
                            </div>
                            <span className="badge bg-primary">{college.category}</span>
                          </div>
                        </div>
                        
                        <div className="d-flex align-items-center gap-1">
                          <Star className="star-rating" size={16} fill="currentColor" />
                          <span className="fw-semibold">{college.rating}</span>
                        </div>
                      </div>

                      <p className="text-muted small mb-3 line-clamp-2">
                        {college.description}
                      </p>

                      <div className="row g-2 text-small mb-3">
                        <div className="col-6">
                          <span className="text-muted">Fees:</span>
                          <div className="fw-semibold text-dark small">{college.fees}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Type:</span>
                          <div className="fw-semibold text-dark small">{college.type}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Established:</span>
                          <div className="fw-semibold text-dark small">{college.established}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Cutoff:</span>
                          <div className="fw-semibold text-dark small">{college.cutoff}</div>
                        </div>
                      </div>

                      <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-3 text-muted small">
                            {college.contact_phone && (
                              <div className="d-flex align-items-center gap-1">
                                <Phone size={12} />
                                <span style={{ fontSize: '0.75rem' }}>{college.contact_phone}</span>
                              </div>
                            )}
                            {college.contact_website && (
                              <a
                                href={college.contact_website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex align-items-center gap-1 text-primary text-decoration-none"
                              >
                                <Globe size={12} />
                                <span style={{ fontSize: '0.75rem' }}>Website</span>
                              </a>
                            )}
                          </div>
                          
                          <Link
                            to={`/college/${college.id}`}
                            className="text-primary fw-semibold text-decoration-none small"
                          >
                            View Details →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {colleges.length === 0 && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <Search className="text-muted mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  No colleges found in {currentState.name} 
                  {selectedStream !== 'all' && ` for ${selectedStream} stream`}.
                  Try selecting a different stream or check back later.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <button 
                    onClick={() => setSelectedStream('all')}
                    className="btn btn-primary"
                  >
                    View All Streams
                  </button>
                  <Link to="/college-reviews" className="btn btn-outline-primary">
                    Browse All Colleges
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StateColleges;